/* eslint-disable react/jsx-handler-names */
/* eslint-disable react/require-default-props */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useSnackbar } from '@/hooks/useSnackbar';
import HomeSetupScreen, { HomeFormData } from '@/pages/AtHomeScreen/components/HomeSetUpScreen';
import LocationSelection from '@/pages/AtHomeScreen/components/LocationSelection';
import OtpVerification from '@/pages/AtHomeScreen/components/OtpVerification';
import SchoolSetupScreen from '@/pages/AtHomeScreen/components/SchoolSetUpScreen';
import useAtHomeScreen from '@/pages/AtHomeScreen/hooks/useAtHomeScreen';
import { CenterValidationFormData } from '@/pages/CenterValidation/components/CenterValidationDialog';
import { ErrorsState } from '@/pages/CenterValidation/hooks/useCenterValidation';
import ProfileScreen from '@/pages/ProfileScreen';
import { isApiResponseSuccess } from '@/utils/helper';

enum LocationEnum {
  Home = 'home',
  School = 'school',
}

enum HomeStep {
  Login = 'login',
  'Otp-Verification' = 'otpVerification',
  Profile = 'profile',
}

const STEP_MAP = {
  login: HomeStep.Login,
  otpVerification: HomeStep['Otp-Verification'],
  profile: HomeStep.Profile,
} as const;

const REVERSE_STEP_MAP = Object.fromEntries(
  Object.entries(STEP_MAP).map(([k, v]) => [v, k]),
) as Record<HomeStep, keyof typeof STEP_MAP>;

interface AtHomeScreenProperties {
  isSubmitting?: boolean;
  onSubmitCenterDetails?: (data: CenterValidationFormData) => void;
  errors?: ErrorsState;
  resetValidation?: (type?: string) => void;
}

function AtHomeScreen({
  errors = {},
  isSubmitting = false,
  resetValidation = () => {},
  onSubmitCenterDetails,
}: AtHomeScreenProperties) {
  const { onSubmitAtHomeAccessCode, onSubmitAtHomeMobile, onSubmitOtp } = useAtHomeScreen();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { showSnackbar } = useSnackbar();
  const [selectedLocation, setSelectedLocation] = useState<LocationEnum | undefined>();
  const [homeStep, setHomeStep] = useState<HomeStep>(HomeStep.Login);
  const [otpData, setOtpData] = useState<{
    mobile: string;
    programCode: string;
    dialCode: string;
    formData: HomeFormData;
    // eslint-disable-next-line unicorn/no-null
  } | null>(null);

  const updateStep = useCallback(
    (step: HomeStep) => {
      const stepName = REVERSE_STEP_MAP[step];
      if (stepName) {
        const parameters: Record<string, string> = { step: stepName };
        setSearchParameters(parameters);
      }
    },
    [setSearchParameters],
  );

  useEffect(() => {
    if (!onSubmitCenterDetails) {
      const step = searchParameters.get('step');
      if (!step) {
        updateStep(HomeStep.Login);
      } else if (step in STEP_MAP) {
        const newStep = STEP_MAP[step as keyof typeof STEP_MAP];
        if (newStep !== homeStep) {
          setHomeStep(newStep);
        }
      }
    }
  }, [searchParameters, homeStep, onSubmitCenterDetails, updateStep]);

  const handleMoveToOtpStep = (
    mobile: string,
    programCode: string,
    dialCode: string,
    formData: HomeFormData,
  ) => {
    setOtpData({ mobile, programCode, dialCode, formData });
    setHomeStep(HomeStep['Otp-Verification']);
    updateStep(HomeStep['Otp-Verification']);
  };

  const handleBackFromOtp = () => {
    setHomeStep(HomeStep.Login);
    // eslint-disable-next-line unicorn/no-null
    setOtpData(null);
    updateStep(HomeStep.Login);
  };

  const handleSubmitAtHomeAccessCode = (formData: HomeFormData) => {
    onSubmitAtHomeAccessCode(formData, (response) => {
      if (isApiResponseSuccess(response)) {
        localStorage.setItem('homeAuthType', 'HOME_ACCESS_CODE');
        updateStep(HomeStep.Profile);
      }
    });
  };

  const handleSubmitAtHomeMobile = (formData: HomeFormData, dialCode: string) => {
    onSubmitAtHomeMobile({ ...formData, mobile: `${dialCode}${formData.mobile}` }, (response) => {
      if (isApiResponseSuccess(response)) {
        localStorage.setItem('homeAuthType', 'HOME_MOBILE');
        handleMoveToOtpStep(formData.mobile, formData.programCode, dialCode, formData);
      }
    });
  };

  const handleResendOtp = () => {
    if (otpData) {
      onSubmitAtHomeMobile(
        { ...otpData.formData, mobile: `${otpData.dialCode}${otpData.mobile}` },
        (response) => {
          if (isApiResponseSuccess(response)) {
            showSnackbar('Otp sent successfully.');
          }
        },
      );
    }
  };
  if (onSubmitCenterDetails) {
    if (selectedLocation === LocationEnum.School) {
      return (
        <SchoolSetupScreen
          errors={errors}
          isSubmitting={isSubmitting}
          resetValidation={resetValidation}
          onSubmitCenterDetails={onSubmitCenterDetails}
        />
      );
    }
    return <LocationSelection onLocationSelected={setSelectedLocation} />;
  }

  if (homeStep === HomeStep.Profile) {
    return <ProfileScreen />;
  }

  if (homeStep === HomeStep['Otp-Verification'] && otpData) {
    return (
      <OtpVerification
        mobile={otpData.mobile}
        dialCode={otpData.dialCode}
        onBack={handleBackFromOtp}
        onSubmitOtp={onSubmitOtp}
        onNavigateToProfile={() => updateStep(HomeStep.Profile)}
        onResendOtp={handleResendOtp}
      />
    );
  }

  return (
    <HomeSetupScreen
      onSubmitAtHomeAccessCode={handleSubmitAtHomeAccessCode}
      onSubmitAtHomeMobile={handleSubmitAtHomeMobile}
    />
  );
}
export default AtHomeScreen;
